import React from 'react';
import { useQuery } from '@apollo/client';
import { Spinner, ErrorMessage } from '@gsa/afp-component-library';
import { GET_MULTIPLE_OPTIONS_FOR_BID } from '../bids.gql';
import ReactHtmlParser from 'react-html-parser';
import PropTypes from 'prop-types';
import { emDashUnicode } from '../../../utilities/constants';
import {
  getLookupLabel,
  getReadableAttribute,
  TABLE_HEADER,
  formatValueForDisplay,
  formatMinimumAttributeDisplay,
} from './utils/notification-center-utils';

const BidChangeNotificationTableSubRow = ({ row }) => {
  const options = [
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      filter: {
        operator: 'EQ',
        key: 'standardCodeMetadataId',
        value: '9',
      },
      uniqueKey: 'fedStandardCode',
    },
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      filter: {
        operator: 'EQ',
        key: 'standardCodeMetadataId',
        value: '5',
      },
      uniqueKey: 'criteria',
    },
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      filter: { operator: 'EQ', key: 'standardCodeMetadataId', value: '14' },
      uniqueKey: 'units',
    },
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      filter: {
        operator: 'EQ',
        key: 'standardCodeMetadataId',
        value: '7',
      },
      uniqueKey: 'quantityRequired',
    },
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      limit: 1000,
      filter: {
        operator: 'EQ',
        key: 'standardCodeMetadataId',
        value: '6',
      },
      uniqueKey: 'category',
    },
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      filter: {
        operator: 'EQ',
        key: 'standardCodeMetadataId',
        value: '2',
      },
      uniqueKey: 'associationType',
    },
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      filter: {
        operator: 'EQ',
        key: 'standardCodeMetadataId',
        value: '4',
      },
      uniqueKey: 'inputType',
    },
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      filter: {
        operator: 'EQ',
        key: 'standardCodeMetadataId',
        value: '5',
      },
      uniqueKey: 'criteria',
    },
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      filter: {
        operator: 'EQ',
        key: 'standardCodeMetadataId',
        value: '3',
      },
      uniqueKey: 'tags',
    },
  ];

  const {
    data,
    error,
    loading: optionsLoading,
  } = useQuery(GET_MULTIPLE_OPTIONS_FOR_BID, {
    fetchPolicy: 'cache-first',
    variables: {
      options,
    },
  });

  if (optionsLoading)
    return (
      <div>
        <Spinner />
      </div>
    );
  if (error)
    return (
      <div>
        <ErrorMessage>
          Something went wrong while loading standard lookup codes.
        </ErrorMessage>
      </div>
    );

  const DataComparisonTable = ({ oldData, newData }) => {
    // Helper function to check if minimum-related data has changed
    const hasMinimumDataChanged = () => {
      if (!oldData || !newData) return false;

      const minimumRelatedFields = [
        'Minimum',
        'lowerBound',
        'upperBound',
        'criteriaCode',
        'preDefinedValue',
        'unit',
      ];

      return minimumRelatedFields.some((field) => {
        const oldValue = oldData[field];
        const newValue = newData[field];

        // Handle deep comparison for preDefinedValue object
        if (field === 'preDefinedValue') {
          return JSON.stringify(oldValue) !== JSON.stringify(newValue);
        }

        return oldValue !== newValue;
      });
    };

    // Helper function to determine if attribute should have yellow background
    const shouldHighlightAttribute = (attribute) => {
      if (!oldData) return false;

      if (attribute === 'Minimum') {
        return hasMinimumDataChanged();
      }

      return oldData[attribute] !== newData[attribute];
    };

    return (
      <>
        <div className="grid-row border-bottom padding-bottom-1">
          <div className="grid-col flex-1 text-bold">
            {TABLE_HEADER.Attribute}
          </div>
          <div className="grid-col flex-1 text-bold">
            {TABLE_HEADER.PreviousValue}
          </div>
          <div className="grid-col flex-1 text-bold">
            {TABLE_HEADER.NewValue}
          </div>
        </div>

        {newData ? (
          Object.keys(newData)
            .filter(
              (attribute) =>
                ![
                  'lowerBound',
                  'upperBound',
                  'criteriaCode',
                  'preDefinedValue',
                ].includes(attribute),
            )
            .map((attribute) => (
              <div className="grid-row padding-top-1" key={attribute}>
                {/* Attribute */}
                <div className="grid-col flex-1">
                  {getReadableAttribute((attribute && attribute) || '')}
                </div>

                {/* PreviousValue */}
                <div
                  className={`grid-col flex-1${
                    shouldHighlightAttribute(attribute) ? ' bg-yellow' : ''
                  }`}
                >
                  {attribute === 'Minimum'
                    ? oldData &&
                      formatMinimumAttributeDisplay(
                        oldData,
                        data?.getMultipleOptions || [],
                      )
                    : ReactHtmlParser(
                        formatValueForDisplay(
                          oldData &&
                            getLookupLabel(
                              attribute,
                              oldData[attribute],
                              data?.getMultipleOptions || [],
                            ),
                        ),
                      )}
                </div>

                {/* NewValue */}
                <div
                  className={`grid-col flex-1${
                    shouldHighlightAttribute(attribute) ? ' bg-yellow' : ''
                  }`}
                >
                  {attribute === 'Minimum'
                    ? formatMinimumAttributeDisplay(
                        newData,
                        data?.getMultipleOptions || [],
                      )
                    : ReactHtmlParser(
                        formatValueForDisplay(
                          getLookupLabel(
                            attribute,
                            newData[attribute],
                            data?.getMultipleOptions || [],
                          ),
                        ),
                      )}
                </div>
              </div>
            ))
        ) : (
          <div className="grid-row padding-top-1">
            <div className="grid-col flex-1">{emDashUnicode}</div>
            <div className="grid-col flex-1">{emDashUnicode}</div>
            <div className="grid-col flex-1">{emDashUnicode}</div>
          </div>
        )}
      </>
    );
  };

  DataComparisonTable.propTypes = {
    oldData: PropTypes.objectOf(PropTypes.any).isRequired,
    newData: PropTypes.objectOf(PropTypes.any).isRequired,
  };

  return (
    <>
      <DataComparisonTable
        oldData={row.original?.oldValue}
        newData={row.original?.newValue}
      />
    </>
  );
};

BidChangeNotificationTableSubRow.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.objectOf(PropTypes.any),
  }).isRequired,
};

export default BidChangeNotificationTableSubRow;
